import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import Select from "components/common/Select";
import { toIsoUtc } from "../chiefComplaints/dateHelpers";
import MedicineService from "./MedicineService";
import UnitPicker from "./UnitPicker";
import {
  SegStrip,
  AS_NEEDED,
  clampDoseValue,
  isPositiveDose,
  fetchFormulationCatalog,
  getFormulationCatalog,
} from "./MedicineForm";
import DiagnosisPicker from "./DiagnosisPicker";
import { MED_V2_REFRESH } from "./events";
import {
  MEDICINE_STATUS,
  medicineBand,
  DOSE_QUICKPICK,
  TIMES_OF_DAY,
  INTERVAL_OPTIONS,
  FREQ_UNIT_OPTIONS,
  INSTRUCTION_OPTIONS,
  DURATION_DAYS_QUICKPICK,
  DURATION_LONG_QUICKPICK,
} from "./data";

/**
 * Update sheet for one medicine — the centred UpdateStatusModal moved into
 * the editor rail beside the Add sheet, the same treatment every other v3
 * section's update surface got. Same med3 control vocabulary as the Add
 * sheet (board 2964:4270); the fields are the modal's — the full regimen is
 * editable on an update so the audit captures the complete new state, with
 * the three-value status select on top.
 *
 * An update sheet is never blank, so it reports itself filled on mount, and
 * it owns its write: a just-in-time version read, then POST …/status.
 */

const MAX_DESCRIPTION_WORDS = 250;
const countWords = (s) => {
  const t = (s || "").trim();
  return t ? t.split(/\s+/).length : 0;
};
const clampWords = (next, max) => {
  if (countWords(next) <= max) return next;
  const parts = next.split(/(\s+)/);
  let words = 0;
  const out = [];
  for (const chunk of parts) {
    if (/^\s+$/.test(chunk)) { out.push(chunk); continue; }
    if (chunk === "") continue;
    if (words >= max) break;
    out.push(chunk);
    words += 1;
  }
  return out.join("").replace(/\s+$/, "");
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const CollapseChevron = () => (
  <svg viewBox="0 0 11 11" width="16" height="16" fill="none" aria-hidden="true">
    <path
      d="M2 7.5L5.5 3.5L9 7.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CalendarIcon = () => (
  <svg viewBox="0 0 16 16" width="15" height="15" fill="none" aria-hidden="true">
    <path
      d="M13.3333 2H12.6667V0.666667H11.3333V2H4.66667V0.666667H3.33333V2H2.66667C1.93333 2 1.33333 2.6 1.33333 3.33333V14C1.33333 14.7333 1.93333 15.3333 2.66667 15.3333H13.3333C14.0667 15.3333 14.6667 14.7333 14.6667 14V3.33333C14.6667 2.6 14.0667 2 13.3333 2ZM13.3333 14H2.66667V6.66667H13.3333V14ZM13.3333 5.33333H2.66667V3.33333H13.3333V5.33333Z"
      fill="#26284D"
    />
  </svg>
);

const MedicineStatusForm = ({
  patientId,
  formId,
  editing,
  ordinal,
  showOrdinal,
  blockedBySibling,
  onFilledChange,
  onSavingChange,
  onClose,
}) => {
  const medicine = editing;
  const cur = (medicine && medicine.current) || {};
  const dq = cur.doseQuantity || {};
  const df = cur.doseFrequency || {};

  const [status, setStatus] = useState(medicine.status || "ongoing");
  // Everything below seeds STRICTLY from the existing record — no
  // synthesised fallback (e.g. "today" / "before" / 7-day) so a genuinely
  // empty field stays empty and the clinician has to pick, matching the
  // Add-sheet rule.
  const [medStartDate, setMedStartDate] = useState(
    cur.medStartDate ? cur.medStartDate.slice(0, 10) : ""
  );
  const [updatedOn, setUpdatedOn] = useState(todayISO); // becomes sinceWhen
  const [ongoing, setOngoing] = useState(cur.ongoingIndefinitely || false);
  const [durationDays, setDurationDays] = useState(cur.durationDays || 0);
  const [customDays, setCustomDays] = useState("");
  const [doseValue, setDoseValue] = useState(dq.value || "");
  const [doseUnit, setDoseUnit] = useState(dq.unit || "");
  // Redesigned dose model (mirrors MedicineForm) — see field comments
  // on model.MedicineState. Per-slot map is populated when the record
  // was saved in Times-of-day mode with named slots; empty otherwise.
  const [perSlotQuantities, setPerSlotQuantities] = useState(
    () => (cur.perSlotQuantities && typeof cur.perSlotQuantities === "object")
      ? { ...cur.perSlotQuantities }
      : {},
  );
  const [timesOfDay, setTimesOfDay] = useState(df.timesOfDay || []);
  const [customFreqValue, setCustomFreqValue] = useState(
    df.customValue ? String(df.customValue) : ""
  );
  const [customFreqUnit, setCustomFreqUnit] = useState(df.customUnit || "Hours");
  // Explicit mode toggle for the whole frequency block. Seeded from
  // whatever the record already carries: perSlot or named timesOfDay
  // slots → "timesOfDay"; custom pair → "custom".
  const [frequencyMode, setFrequencyMode] = useState(() => {
    const hasSlots = (df.timesOfDay || []).length > 0;
    const hasPerSlot = cur.perSlotQuantities && Object.keys(cur.perSlotQuantities || {}).length > 0;
    if (hasSlots || hasPerSlot) return "timesOfDay";
    if (df.customValue) return "custom";
    return "timesOfDay";
  });
  // Formulation catalog — REFLD → { label, units, instructions }.
  // Reuses the module-level cache in MedicineForm so every form
  // instance shares ONE fetch.
  const [formulationCatalog, setFormulationCatalog] = useState(
    () => getFormulationCatalog() || {},
  );
  useEffect(() => {
    let alive = true;
    fetchFormulationCatalog().then((r) => {
      if (!alive || !r) return;
      if (Object.keys(r).length) setFormulationCatalog(r);
    });
    return () => { alive = false; };
  }, []);
  const [instructions, setInstructions] = useState(cur.instructions || "");
  const [diagnosisId, setDiagnosisId] = useState(medicine.diagnosisId || null);
  // Seed Description from the existing record so the clinician sees the
  // note that was recorded on the last write — matches the "load all
  // config to fields on update" rule.
  const [reason, setReason] = useState(cur.description || "");

  const [collapsed, setCollapsed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  // Seed the "which duration cell was last picked" ref from whatever the
  // record has — blank when the record has no duration so nothing lights
  // up spuriously.
  const lastPickRef = useRef(cur.durationDays ? String(cur.durationDays) : "");

  // Rail contract: an update sheet is never blank.
  useEffect(() => {
    onFilledChange(formId, true);
  }, [formId, onFilledChange]);

  // Same mutex + mode-toggle rules as the Add sheet — see MedicineForm
  // for the full rationale.
  // Preserve the other mode's state on switch — the reviewer can
  // toggle to preview the alternate shape (or restore an earlier pick)
  // without losing what's already there. The save body filters by
  // `frequencyMode` so only the active mode's values reach the wire.
  const switchFrequencyMode = (next) => {
    if (next === frequencyMode) return;
    setFrequencyMode(next);
  };

  // Seed unit for a NEW per-slot row — same rule as MedicineForm:
  // picked scalar unit > single Recommended unit > "" (picker shows
  // the "None"/"unit" placeholder).
  const seedSlotUnit = () => doseUnit || (availableUnits.length === 1 ? availableUnits[0] : "");

  // Per-slot quantity setters — mirror MedicineForm's UX-only fan-out.
  // First filled slot copies its value / unit to every other selected
  // slot IF the others are all empty. Once any other slot carries a
  // value / unit the copy stops firing, so a differentiated regimen
  // the reviewer set intentionally is never overwritten.
  const setSlotValue = (slot, value) => {
    setPerSlotQuantities((prev) => {
      // Guard: a None-unit slot never carries a qty. If the picker
      // somehow calls onChange on a disabled input (autofill, paste,
      // race with the disabled attr taking effect), swallow the value.
      const currentUnit = (prev[slot] && prev[slot].unit) || "";
      if (!currentUnit) return prev;
      // Rule 1: qty change NEVER touches units — this slot's unit is
      // preserved as-is, and the fan-out below leaves every other slot's
      // unit alone. A qty change is a qty change.
      const nextRow = { value, unit: currentUnit };
      // Rule 3: cascade the qty into the slots BELOW only when the
      // source is the FIRST slot AND every (non-None) below slot still
      // tracks the source's PREVIOUS value. That gives keystroke-by-
      // keystroke mirroring ("4" → "45" → "456") while the reviewer is
      // typing at the top; the moment they touch any below slot the
      // mirror stops. Typing in any slot other than the first is always
      // row-local — no cascade. None-unit below slots are ignored.
      const isFirstSlot = namedSlots.length > 1 && slot === namedSlots[0];
      const sourceOld = (prev[slot] && prev[slot].value) || "";
      const fanOut =
        isFirstSlot &&
        namedSlots.every((s) => {
          if (s === slot) return true;
          const su = (prev[s] && prev[s].unit) || "";
          if (!su) return true;
          return ((prev[s] && prev[s].value) || "") === sourceOld;
        });
      if (!fanOut) return { ...prev, [slot]: nextRow };
      const next = { ...prev, [slot]: nextRow };
      namedSlots.forEach((s) => {
        if (s === slot) return;
        const siblingUnit = (prev[s] && prev[s].unit) || "";
        // A sibling whose unit is None never receives the cascaded qty.
        // Preserve its own value (which should already be blank because
        // the qty box is disabled when unit is None).
        if (!siblingUnit) {
          next[s] = { value: (prev[s] && prev[s].value) || "", unit: "" };
          return;
        }
        // Preserve sibling's own unit (rule 1 forbids us from touching
        // it here).
        next[s] = { value, unit: siblingUnit };
      });
      return next;
    });
  };
  const setSlotUnit = (slot, unit) => {
    setPerSlotQuantities((prev) => {
      // Picking None (empty unit) clears the paired Qty so a stale value
      // doesn't linger in a greyed-out box on save/reload.
      const value = unit ? ((prev[slot] && prev[slot].value) || "") : "";
      const nextRow = { value, unit };
      // Rule 4: cascade the unit only from the FIRST slot and only
      // while every below slot is still on None. Once any below slot
      // has its own unit picked, unit changes on the first slot stay
      // row-local. Picking None on the first slot also stays local
      // (None doesn't cascade).
      const isFirstSlot = namedSlots.length > 1 && slot === namedSlots[0];
      const fanOut =
        isFirstSlot &&
        String(unit).trim() !== "" &&
        namedSlots.every((s) => s === slot
          ? true
          : !String((prev[s] && prev[s].unit) || "").trim());
      if (!fanOut) return { ...prev, [slot]: nextRow };
      const next = { ...prev, [slot]: nextRow };
      namedSlots.forEach((s) => {
        if (s === slot) return;
        next[s] = { value: (prev[s] && prev[s].value) || "", unit };
      });
      return next;
    });
  };

  const toggleTimeOfDay = (t) => {
    if (t === AS_NEEDED) {
      setTimesOfDay((prev) => {
        const has = prev.includes(AS_NEEDED);
        if (has) return prev.filter((x) => x !== AS_NEEDED);
        return [AS_NEEDED];
      });
      setPerSlotQuantities({});
      return;
    }
    const seedUnit = seedSlotUnit();
    setTimesOfDay((prev) => {
      const withoutAsNeeded = prev.filter((x) => x !== AS_NEEDED);
      if (withoutAsNeeded.includes(t)) {
        setPerSlotQuantities((qs) => {
          const { [t]: _unused, ...rest } = qs;
          return rest;
        });
        return withoutAsNeeded.filter((x) => x !== t);
      }
      setPerSlotQuantities((qs) => ({
        ...qs,
        [t]: qs[t] || { value: "", unit: seedUnit },
      }));
      return [...withoutAsNeeded, t];
    });
  };
  // Interval chips are pure shortcuts for the custom (value + unit)
  // pair below. See MedicineForm.pickInterval for the full rationale.
  const pickInterval = (iv) => {
    const n = iv.replace(/h$/, "");
    if (customFreqValue === n && customFreqUnit === "Hours") {
      setCustomFreqValue("");
      return;
    }
    setCustomFreqValue(n);
    setCustomFreqUnit("Hours");
  };
  const intervalChipMatches = (iv) => {
    const n = iv.replace(/h$/, "");
    return customFreqValue === n && customFreqUnit === "Hours";
  };
  // Per-unit bounds — mirrors MedicineForm:
  //   Hours              → [1, 23]
  //   Days/Weeks/Months  → [1, 999]
  const customFreqMax = (u) => (u === "Hours" ? 23 : 999);
  const clampCustomFreq = (v, u) => {
    if (v === "") return v;
    const n = parseInt(v, 10);
    if (isNaN(n)) return v;
    const max = customFreqMax(u);
    if (n > max) return String(max);
    if (n < 1)   return "1";
    return v;
  };
  const changeCustomFreqValue = (v) => {
    setCustomFreqValue(clampCustomFreq(v, customFreqUnit));
  };
  const changeCustomFreqUnit = (u) => {
    if (customFreqValue) {
      const clamped = clampCustomFreq(customFreqValue, u);
      if (clamped !== customFreqValue) setCustomFreqValue(clamped);
    }
    setCustomFreqUnit(u);
  };

  const pickDays = (n) => {
    setDurationDays(n);
    setCustomDays("");
  };

  // Ending a course (status → completed/discontinued) locks the regimen
  // block — dose, frequency, instructions, duration, start-date — because
  // those describe an active regimen. Only Updated on + Description stay
  // editable so the audit can capture WHEN the course ended and WHY.
  const isEnded = status !== "ongoing";
  const regimenDisabled = submitting || isEnded;
  // Past medicines (Completed / Discontinued) are FIXED-state: the
  // clinician can only add an audit note (Updated on + Description).
  // Reopening / reclassifying flows through the Add-medicine sheet
  // instead — same mCode dedup triggers a reactivation. Freezing the
  // Status dropdown here keeps the two paths from overlapping.
  const wasPast = medicine.status === "completed" || medicine.status === "discontinued";
  const statusDisabled = submitting || wasPast;

  // Snapshot of the SEEDED form values at mount — used by the dirty check
  // below to disable Save until the clinician actually changes something.
  // Prevents no-op audit entries. Kept in a ref so it survives re-renders
  // but never re-captures on state updates.
  const initialRef = useRef(null);
  if (initialRef.current === null) {
    initialRef.current = {
      status: medicine.status || "ongoing",
      medStartDate: cur.medStartDate ? cur.medStartDate.slice(0, 10) : "",
      updatedOn: todayISO(),
      ongoing: !!cur.ongoingIndefinitely,
      durationDays: cur.durationDays || 0,
      customDays: "",
      doseValue: dq.value || "",
      doseUnit: dq.unit || "",
      perSlotQuantitiesJSON: JSON.stringify(cur.perSlotQuantities || {}),
      frequencyMode: (() => {
        const hasSlots = (df.timesOfDay || []).length > 0;
        const hasPerSlot = cur.perSlotQuantities && Object.keys(cur.perSlotQuantities || {}).length > 0;
        if (hasSlots || hasPerSlot) return "timesOfDay";
        if (df.customValue) return "custom";
        return "timesOfDay";
      })(),
      timesOfDay: (df.timesOfDay || []).slice(),
      customFreqValue: (customFreqValue || ""),
      customFreqUnit: (customFreqUnit || "Hours"),
      instructions: cur.instructions || "",
      diagnosisId: medicine.diagnosisId || null,
      reason: cur.description || "",
    };
  }

  const effectiveDurationDays = ongoing
    ? 0
    : customDays
    ? Math.max(0, parseInt(customDays, 10) || 0)
    : durationDays;

  // Custom-frequency pair rule — same as the Add sheet: value + unit are
  // both-or-neither. The pair check runs even when the regimen block is
  // locked (isEnded) because the pair might have been half-typed before
  // the clinician flipped Status back to Completed / Discontinued.
  const customFreqPairOK = !customFreqValue || !!customFreqUnit;

  // Frequency validity depends on mode — same rule as MedicineForm.
  const hasFrequency =
    frequencyMode === "timesOfDay"
      ? timesOfDay.length > 0
      : !!customFreqValue && !!customFreqUnit;

  // As-needed / per-slot dose rules — mirror MedicineForm. Ointment
  // family formulations use the same Qty + unit controls as any other
  // formulation; the "Local application" chip lives in Instructions,
  // NOT in the Dose block.
  const catalogEntry = formulationCatalog[medicine.formulation];
  const availableUnits = (catalogEntry && catalogEntry.units) || [];
  const catalogInstructions = (catalogEntry && catalogEntry.instructions) || [];
  // Global unit vocabulary — same derivation as MedicineForm.
  const allUnits = React.useMemo(() => {
    const s = new Set();
    Object.values(formulationCatalog).forEach((e) => {
      if (!e || !Array.isArray(e.units)) return;
      e.units.forEach((u) => { if (u) s.add(u); });
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [formulationCatalog]);
  const otherUnits = allUnits.filter((u) => !availableUnits.includes(u));
  const availableInstructions = catalogInstructions
    .map((k) => INSTRUCTION_OPTIONS.find((o) => o.key === k))
    .filter(Boolean);
  // Auto-align scalar + per-slot units with the formulation's
  // Recommended list on every formulation change — mirrors MedicineForm.
  // 0-unit case clears the previous pick so a stale unit doesn't
  // linger when switching between medicines.
  useEffect(() => {
    // If the formulation catalog hasn't landed yet, `availableUnits` is
    // still []. Don't run the reset logic in that window — it would
    // blank the record's saved unit before the catalog arrives, and the
    // later "preserve on load" branches (below) would then keep that
    // blank forever. Wait for a real catalog entry before deciding.
    if (!catalogEntry) return;
    if (availableUnits.length === 1) {
      // Same preserve-None rule as the `> 1` branch below: an empty
      // (None) saved unit stays empty on Update; only a truthy-but-
      // stale unit gets replaced with the single Recommended one.
      const only = availableUnits[0];
      const keep = (prev) => (!prev || availableUnits.includes(prev) ? prev : only);
      setDoseUnit(keep);
      setPerSlotQuantities((prev) => {
        const next = {};
        Object.keys(prev).forEach((slot) => {
          next[slot] = { value: prev[slot].value, unit: keep(prev[slot].unit) };
        });
        return next;
      });
    } else if (availableUnits.length > 1) {
      // Preserve the saved unit for the Update form — including an
      // explicit None (empty string) that the reviewer chose. Only replace
      // a TRUTHY-but-stale unit (a value that no longer belongs to the
      // Recommended set for the current formulation) with the first
      // Recommended default. None ("") is a legitimate persisted state,
      // not a "missing" value.
      const first = availableUnits[0];
      const keep = (prev) => (!prev || availableUnits.includes(prev) ? prev : first);
      setDoseUnit(keep);
      setPerSlotQuantities((prev) => {
        const next = {};
        Object.keys(prev).forEach((slot) => {
          next[slot] = { value: prev[slot].value, unit: keep(prev[slot].unit) };
        });
        return next;
      });
    } else {
      setDoseUnit("");
      setPerSlotQuantities((prev) => {
        const next = {};
        Object.keys(prev).forEach((slot) => {
          next[slot] = { value: prev[slot].value, unit: "" };
        });
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicine.formulation, availableUnits.join("|")]);
  // Keep the instruction pick consistent with the formulation's
  // declared options. Instructions are optional (user may pick or
  // clear any chip), so we never auto-select — we only clear a pick
  // that no longer belongs to the current formulation's chip set.
  useEffect(() => {
    if (availableInstructions.length === 0) {
      if (instructions) setInstructions("");
      return;
    }
    if (instructions && !availableInstructions.some((o) => o.key === instructions)) {
      setInstructions("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [medicine.formulation, availableInstructions.length, availableInstructions.map((o) => o.key).join("|")]);
  const timesOfDayHasAsNeeded = timesOfDay.includes(AS_NEEDED);
  const namedSlots = timesOfDay
    .filter((t) => t !== AS_NEEDED)
    .sort((a, b) => TIMES_OF_DAY.indexOf(a) - TIMES_OF_DAY.indexOf(b));
  // "None" unit (empty string) opts the slot out of the dose check —
  // the Qty box is greyed / disabled in that state, so requiring a
  // positive number would leave a legitimate None-unit save unreachable.
  const scalarDoseOK = !doseUnit || isPositiveDose(doseValue);
  const perSlotDoseOK =
    namedSlots.length === 0 ||
    namedSlots.every((s) => {
      const q = perSlotQuantities[s] || {};
      return !q.unit || isPositiveDose(q.value);
    });
  const hasDose =
    frequencyMode === "timesOfDay"
      ? (timesOfDayHasAsNeeded ? scalarDoseOK : perSlotDoseOK)
      : scalarDoseOK;

  // Dirty check — Save is disabled until the clinician actually changes
  // something. Prevents no-op audit noise when the modal is opened and
  // closed without edits. Compares CURRENT form state to the mount-time
  // snapshot in initialRef.
  const arrEq = (a, b) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
    return true;
  };
  const init = initialRef.current;
  const isDirty =
    status !== init.status ||
    medStartDate !== init.medStartDate ||
    updatedOn !== init.updatedOn ||
    ongoing !== init.ongoing ||
    durationDays !== init.durationDays ||
    customDays !== init.customDays ||
    doseValue !== init.doseValue ||
    doseUnit !== init.doseUnit ||
    JSON.stringify(perSlotQuantities) !== init.perSlotQuantitiesJSON ||
    frequencyMode !== init.frequencyMode ||
    !arrEq(timesOfDay, init.timesOfDay) ||
    customFreqValue !== init.customFreqValue ||
    customFreqUnit !== init.customFreqUnit ||
    instructions !== init.instructions ||
    diagnosisId !== init.diagnosisId ||
    reason !== init.reason;

  // Same required-field rule as the Add sheet — with a carve-out for the
  // regimen block when the course is being ended: dose / duration /
  // instructions / start-date belong to an ACTIVE regimen, so completing
  // or discontinuing a medicine shouldn't demand they be non-empty (the
  // clinician might be recording a legacy row where they weren't set).
  // Guard against a manually-typed pre-1900 date sneaking past the
  // native picker's min on either date input.
  const startDateInvalid = !!medStartDate && medStartDate < "1900-01-01";
  const updatedOnInvalid = !!updatedOn && updatedOn < "1900-01-01";
  // Updated on must land on or after the medicine's Start date — an
  // update event can't happen before the medicine started. When both
  // are present, that lower bound wins over the 1900 floor.
  const updatedOnBeforeStart =
    !!updatedOn && !!medStartDate && updatedOn < medStartDate;
  const canSubmit =
    isDirty &&
    !!updatedOn &&
    !updatedOnInvalid &&
    !updatedOnBeforeStart &&
    customFreqPairOK &&
    !submitting &&
    !blockedBySibling &&
    (isEnded ||
      (hasDose &&
        !!medStartDate &&
        !startDateInvalid &&
        hasFrequency &&
        (ongoing || effectiveDurationDays > 0)));

  const close = () => onClose(formId);

  const submit = () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setFeedback(null);
    onSavingChange(formId, true);

    const svc = new MedicineService();
    const done = () => {
      if (!mounted.current) return;
      onSavingChange(formId, false);
      setSubmitting(false);
      EventService.emit(MED_V2_REFRESH);
      onClose(formId);
    };
    const failed = (err) => {
      if (!mounted.current) return;
      onSavingChange(formId, false);
      setSubmitting(false);
      if (err && err.reason === "version") EventService.emit(MED_V2_REFRESH);
      setFeedback({
        kind: "err",
        text: (err && err.message) || "Update failed — please retry",
      });
    };
    // Only ONE dose shape carried per save (see MedicineForm's submit
    // body for the full rationale). Times-of-day + named slots →
    // perSlotQuantities; everything else → scalar doseQuantity.
    const usePerSlot = frequencyMode === "timesOfDay" && namedSlots.length > 0;
    const perSlotClean = usePerSlot
      ? namedSlots.reduce((acc, s) => {
          const q = perSlotQuantities[s] || {};
          acc[s] = { value: String(q.value || "").trim(), unit: String(q.unit || "").trim() };
          return acc;
        }, {})
      : undefined;
    // Times-of-day + As-needed carries a real scalar (updated spec).
    // Ointment / Custom mode also scalar. Per-slot mode leaves the
    // scalar undefined.
    const scalarDose = usePerSlot
      ? undefined
      : { value: doseValue.trim(), unit: doseUnit.trim() };

    const body = {
      status,
      sinceWhen: toIsoUtc(updatedOn),
      medStartDate: medStartDate ? toIsoUtc(medStartDate) : undefined,
      // On terminate the backend's status-flip semantics own the end date.
      medEndDate: undefined,
      ongoingIndefinitely: ongoing,
      durationDays: effectiveDurationDays,
      doseQuantity: scalarDose,
      perSlotQuantities: perSlotClean,
      // Interval chips are UI shortcuts for the (value+unit) pair, so
      // there's no separate `interval` on the wire. Custom mode sends
      // the pair verbatim; Times-of-day sends only slots.
      doseFrequency: frequencyMode === "timesOfDay"
        ? { timesOfDay, customValue: undefined, customUnit: "" }
        : {
            timesOfDay: [],
            customValue: customFreqValue ? parseInt(customFreqValue, 10) || 0 : undefined,
            customUnit: customFreqUnit,
          },
      instructions,
      diagnosisId: diagnosisId || undefined,
      description: reason.trim(),
    };
    svc.list(
      patientId,
      (doc) => {
        svc.updateStatus(
          patientId,
          medicine.medicineId,
          { ...body, expectedVersion: (doc && doc.version) || 0 },
          done,
          failed
        );
      },
      failed
    );
  };

  const band = status ? medicineBand(status) : null;
  const foldId = `med3-sfold-${formId}`;
  const statusId = `med3-sstat-${formId}`;
  const startId = `med3-sstart-${formId}`;
  const updId = `med3-supd-${formId}`;
  const reasonId = `med3-sreason-${formId}`;

  return (
    <section className={`med3-dlg${collapsed ? " is-collapsed" : ""}`}>
      <div className="med3-lbl-row med3-handle">
        <span className="med3-lbl med3-update-title">
          {showOrdinal && <span className="zy-ord" aria-hidden="true">{ordinal}</span>}
          Update
          <span className="med3-update-sub">&middot; {medicine.medicine}</span>
        </span>
        <button
          type="button"
          className="med3-chev"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
          aria-controls={foldId}
          aria-label={
            collapsed ? `Expand ${medicine.medicine}` : `Collapse ${medicine.medicine}`
          }
        >
          <CollapseChevron />
        </button>
      </div>

      <div className="med3-fold" id={foldId}>
        <div className="med3-fold-inner">
          {/* ── Status — the three-value select with the band dot. Frozen
              for past medicines (Completed / Discontinued): use the Add
              flow's mCode dedup to reopen / reclassify instead. ── */}
          <div
            className={`med3-field med3-fld${band ? " has-dot" : ""}`}
            style={band ? { "--med3-dot": band.dot } : undefined}
          >
            <label className="med3-lbl" htmlFor={statusId}>
              Status <span className="cc3-req">*</span>
              {wasPast && (
                <span className="med3-lbl-hint">
                  &middot; locked · use Add medicine to reopen
                </span>
              )}
            </label>
            <Select
              id={statusId}
              value={status}
              options={MEDICINE_STATUS}
              getKey={(o) => o.key}
              getLabel={(o) => o.label}
              onChange={setStatus}
              disabled={statusDisabled}
            />
          </div>

          {/* ── Regimen block — everything from For through Duration greys
              out when the course is being ended. Wraps in one div so a
              single `is-locked` class can dim + point-events:none the
              whole block from the SCSS. ── */}
          <div className={`med3-regimen${isEnded ? " is-locked" : ""}`}>
          {/* ── For · link to a diagnosed condition (parity with Add) ── */}
          <div className="med3-field">
            <span className="med3-lbl">
              For <span className="med3-lbl-hint">&middot; link to a diagnosed condition</span>
            </span>
            <DiagnosisPicker
              patientId={patientId}
              value={diagnosisId}
              onChange={setDiagnosisId}
              disabled={regimenDisabled}
            />
          </div>

          <div className="med3-field">
            <label className="med3-lbl is-muted" htmlFor={startId}>
              Start date <span className="cc3-req">*</span>
            </label>
            <span className="med3-date">
              <input
                id={startId}
                className="med3-date-input"
                type="date"
                value={medStartDate}
                onChange={(e) => setMedStartDate(e.target.value)}
                min="1900-01-01"
                max={todayISO()}
                disabled={regimenDisabled}
              />
              <span className="med3-date-icon" aria-hidden="true"><CalendarIcon /></span>
            </span>
            {startDateInvalid && (
              <p className="fam-field-error">Start date must be on or after 1 Jan 1900.</p>
            )}
          </div>

          {/* ── Dose frequency — mode toggle + nested card with dose
              quantity inside. Mirrors MedicineForm's redesigned layout
              (mode toggle outside; frequency + dose belong to the same
              bordered card regardless of mode). Regimen-disabled state
              propagates to every input so the whole block locks when
              the course is being ended. ── */}
          <div className="med3-field is-roomy">
            <span className="med3-lbl">
              Dose frequency <span className="cc3-req">*</span>
            </span>
            <div className="med3-mode" role="tablist" aria-label="Dose frequency mode">
              <button
                type="button"
                role="tab"
                aria-selected={frequencyMode === "timesOfDay"}
                className={`med3-mode-btn${frequencyMode === "timesOfDay" ? " is-on" : ""}`}
                onClick={() => switchFrequencyMode("timesOfDay")}
                disabled={regimenDisabled}
              >
                Times of day
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={frequencyMode === "custom"}
                className={`med3-mode-btn${frequencyMode === "custom" ? " is-on" : ""}`}
                onClick={() => switchFrequencyMode("custom")}
                disabled={regimenDisabled}
              >
                Custom
              </button>
            </div>

            <div className="med3-freq-card">
              {frequencyMode === "timesOfDay" ? (
                <>
                  <span className="med3-freq-card-lbl">
                    Times of day <span className="cc3-req">*</span>{" "}
                    <span className="med3-lbl-hint">&middot; multi-select</span>
                  </span>
                  <div className="med3-slot-row" role="group">
                    {TIMES_OF_DAY.map((t) => (
                      <button
                        key={t}
                        type="button"
                        className={`med3-slot-btn${timesOfDay.includes(t) ? " is-on" : ""}`}
                        aria-pressed={timesOfDay.includes(t)}
                        onClick={() => toggleTimeOfDay(t)}
                        disabled={regimenDisabled}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  <p className="med3-freq-or"><span>or</span></p>
                  <button
                    type="button"
                    className={`med3-asneeded-btn${timesOfDayHasAsNeeded ? " is-on" : ""}`}
                    aria-pressed={timesOfDayHasAsNeeded}
                    onClick={() => toggleTimeOfDay(AS_NEEDED)}
                    disabled={regimenDisabled}
                  >
                    As needed
                  </button>
                </>
              ) : (
                <>
                  <span className="med3-freq-card-lbl">
                    Interval <span className="cc3-req">*</span>{" "}
                    <span className="med3-lbl-hint">&middot; single-select</span>
                  </span>
                  <div className="med3-slot-row" role="group">
                    {INTERVAL_OPTIONS.map((iv) => {
                      const on = intervalChipMatches(iv);
                      return (
                        <button
                          key={iv}
                          type="button"
                          className={`med3-slot-btn${on ? " is-on" : ""}`}
                          aria-pressed={on}
                          onClick={() => pickInterval(iv)}
                          disabled={regimenDisabled}
                        >
                          {iv}
                        </button>
                      );
                    })}
                  </div>
                  <div className="med3-two">
                    <div className="med3-field">
                      <input
                        className="med3-input"
                        type="number"
                        min={1}
                        max={customFreqMax(customFreqUnit)}
                        value={customFreqValue}
                        onChange={(e) => changeCustomFreqValue(e.target.value)}
                        placeholder="—"
                        aria-label="Custom frequency value"
                        disabled={regimenDisabled}
                      />
                    </div>
                    <div className="med3-field">
                      <Select
                        value={customFreqUnit}
                        options={FREQ_UNIT_OPTIONS.map((u) => ({ key: u, label: u }))}
                        getKey={(o) => o.key}
                        getLabel={(o) => o.label}
                        onChange={changeCustomFreqUnit}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* ── Dose quantity subsection — nested inside the card. */}
              <div className="med3-dose-head med3-dose-head-nested">
                <span className="med3-freq-card-lbl">
                  Dose quantity<span className="cc3-req">*</span>
                </span>
                <span className="med3-formulation-pill" title="Formulation from the catalog">
                  <span className="med3-formulation-pill-lbl">FORMULATION</span>
                  <span className="med3-formulation-pill-val">
                    {medicine.formulationName
                      || (catalogEntry && catalogEntry.label)
                      || medicine.formulation
                      || "—"}
                  </span>
                </span>
              </div>

              {frequencyMode === "timesOfDay" ? (
                timesOfDayHasAsNeeded ? (
                  // As-needed slot picked: scalar Qty + unit row (no
                  // preset ladder). Same 0/1/N unit renderer.
                  <div className="med3-dose-row">
                    {/* Qty stays disabled while the unit is empty
                        (including an explicit "None") — a quantity
                        without a unit is never a meaningful dose. */}
                    <input
                      className="med3-input"
                      type="text"
                      value={doseValue}
                      onChange={(e) => setDoseValue(clampDoseValue(e.target.value))}
                      placeholder="Qty"
                      aria-label="Dose quantity"
                      disabled={regimenDisabled || !doseUnit}
                    />
                    <UnitPicker
                      value={doseUnit}
                      recommended={availableUnits}
                      other={otherUnits}
                      onChange={(u) => {
                        setDoseUnit(u);
                        // Picking None clears the Qty box — a stale
                        // number sitting in a greyed-out input reads as
                        // a live value on save/reload otherwise.
                        if (!u) setDoseValue("");
                      }}
                      disabled={regimenDisabled}
                      ariaLabel="Dose unit"
                    />
                  </div>
                ) : namedSlots.length === 0 ? (
                  <p className="med3-dose-hint">Select times of day to set quantities</p>
                ) : (
                  <div className="med3-slot-doses">
                    {namedSlots.map((slot) => {
                      const q = perSlotQuantities[slot] || { value: "", unit: "" };
                      return (
                        <div className="med3-slot-dose-row" key={slot}>
                          <span className="med3-slot-dose-lbl">{slot}</span>
                          <input
                            className="med3-input"
                            type="text"
                            value={q.value}
                            onChange={(e) => setSlotValue(slot, clampDoseValue(e.target.value))}
                            placeholder="Qty"
                            aria-label={`${slot} dose quantity`}
                            disabled={regimenDisabled || !q.unit}
                          />
                          <UnitPicker
                            value={q.unit}
                            recommended={availableUnits}
                            other={otherUnits}
                            onChange={(u) => setSlotUnit(slot, u)}
                            disabled={regimenDisabled}
                            ariaLabel={`${slot} dose unit`}
                          />
                        </div>
                      );
                    })}
                  </div>
                )
              ) : (
                <>
                  <SegStrip
                    rows={[DOSE_QUICKPICK.map((v) => ({ key: v, label: v }))]}
                    isOn={(k) => doseValue === k}
                    onToggle={(k) => setDoseValue(doseValue === k ? "" : k)}
                    disabled={regimenDisabled || !doseUnit}
                    boldOn
                  />
                  <div className="med3-dose-row">
                    <input
                      className="med3-input"
                      type="text"
                      value={doseValue}
                      onChange={(e) => setDoseValue(clampDoseValue(e.target.value))}
                      placeholder="Qty"
                      aria-label="Dose quantity"
                      disabled={regimenDisabled || !doseUnit}
                    />
                    <UnitPicker
                      value={doseUnit}
                      recommended={availableUnits}
                      other={otherUnits}
                      onChange={(u) => {
                        setDoseUnit(u);
                        // Picking None clears the Qty box — a stale
                        // number sitting in a greyed-out input reads as
                        // a live value on save/reload otherwise.
                        if (!u) setDoseValue("");
                      }}
                      disabled={regimenDisabled}
                      ariaLabel="Dose unit"
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* ── Instructions ── driven by per-formulation options.
              Standard three chips, single "Local application" for
              ointment-family, or hidden entirely for the future
              "None" case. See MedicineForm for the heuristic. */}
          {availableInstructions.length > 0 && (
            <div className="med3-field">
              <span className="med3-lbl">Instructions</span>
              <div className="med3-chips" role="group" aria-label="Instructions">
                {availableInstructions.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    className={`med3-chip${instructions === o.key ? " is-on" : ""}`}
                    aria-pressed={instructions === o.key}
                    onClick={() =>
                      setInstructions(instructions === o.key ? "" : o.key)
                    }
                    disabled={regimenDisabled}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="med3-field is-roomy">
            <div className="med3-duration-head">
              <span className="med3-lbl">
                Duration <span className="cc3-req">*</span>{" "}
                <span className="med3-lbl-hint">&middot; days / weeks</span>
              </span>
              <label className="med3-toggle-wrap">
                <input
                  type="checkbox"
                  checked={ongoing}
                  onChange={(e) => setOngoing(e.target.checked)}
                  disabled={regimenDisabled}
                />
                <span className="med3-toggle" aria-hidden="true" />
                <span className="med3-toggle-lbl">Ongoing</span>
              </label>
            </div>
            <div className={`med3-duration-stack${ongoing ? " is-dimmed" : ""}`}>
              <SegStrip
                rows={[
                  DURATION_DAYS_QUICKPICK.map((d) => ({ key: String(d), label: String(d) })),
                  DURATION_LONG_QUICKPICK.map((d) => ({ key: d.label, label: d.label })),
                ]}
                isOn={(k) => {
                  if (customDays || ongoing) return false;
                  const long = DURATION_LONG_QUICKPICK.find((d) => d.label === k);
                  if (long) return durationDays === long.days && lastPickRef.current === k;
                  return durationDays === Number(k) && lastPickRef.current === k;
                }}
                onToggle={(k) => {
                  if (ongoing) return;
                  lastPickRef.current = k;
                  const long = DURATION_LONG_QUICKPICK.find((d) => d.label === k);
                  pickDays(long ? long.days : Number(k));
                }}
                disabled={ongoing || regimenDisabled}
                footer={
                  <div className="med3-seg-row med3-seg-custom-row">
                    <input
                      className="med3-seg-custom-input"
                      type="number"
                      min="1"
                      value={customDays}
                      onChange={(e) => setCustomDays(e.target.value)}
                      placeholder="Custom - e.g. 21"
                      aria-label="Custom days"
                      disabled={ongoing || regimenDisabled}
                    />
                    <span className="med3-seg-custom-unit">Days</span>
                  </div>
                }
              />
            </div>
          </div>
          </div>

          <div className="med3-field">
            <label className="med3-lbl is-muted" htmlFor={updId}>
              Updated on <span className="cc3-req">*</span>
            </label>
            <span className="med3-date">
              <input
                id={updId}
                className="med3-date-input"
                type="date"
                value={updatedOn}
                onChange={(e) => setUpdatedOn(e.target.value)}
                // Native picker greys out anything below Start date (or
                // 1 Jan 1900 when there's no Start yet) and anything in
                // the future. A manually-typed value that slips past is
                // caught by the Save guard below.
                min={medStartDate && medStartDate > "1900-01-01" ? medStartDate : "1900-01-01"}
                max={todayISO()}
                disabled={submitting}
              />
              <span className="med3-date-icon" aria-hidden="true"><CalendarIcon /></span>
            </span>
            {updatedOnInvalid && (
              <p className="fam-field-error">Updated on must be on or after 1 Jan 1900.</p>
            )}
            {updatedOnBeforeStart && !updatedOnInvalid && (
              <p className="fam-field-error">
                Updated on cannot be earlier than the medicine&rsquo;s Start date.
              </p>
            )}
          </div>

          <div className="med3-field">
            <label className="med3-lbl med3-desc-lbl" htmlFor={reasonId}>
              Description <span className="med3-lbl-hint">&middot; optional</span>
              <span className="med3-words">
                {reason
                  ? `${countWords(reason)} / ${MAX_DESCRIPTION_WORDS} words`
                  : `Max ${MAX_DESCRIPTION_WORDS} words`}
              </span>
            </label>
            <textarea
              id={reasonId}
              className="med3-textarea"
              rows={3}
              value={reason}
              onChange={(e) => setReason(clampWords(e.target.value, MAX_DESCRIPTION_WORDS))}
              placeholder="Provide description here"
              disabled={submitting}
            />
          </div>

          {feedback && (
            <div className={`cc-feedback is-${feedback.kind}`}>{feedback.text}</div>
          )}

          <footer className="med3-foot">
            {/* Never disabled while saving — see the Add sheet. */}
            <button type="button" className="med3-btn med3-btn-sec" onClick={close}>
              {submitting ? "Close" : "Cancel"}
            </button>
            <button
              type="button"
              className="med3-btn med3-btn-pri"
              onClick={submit}
              disabled={!canSubmit}
              title={blockedBySibling ? "Another medicine is saving" : undefined}
            >
              {submitting ? "Updating…" : "Update"}
            </button>
          </footer>
        </div>
      </div>
    </section>
  );
};

MedicineStatusForm.propTypes = {
  patientId: PropTypes.number,
  formId: PropTypes.number.isRequired,
  /** The medicine being updated (carries the rail's mode marker). */
  editing: PropTypes.object.isRequired,
  ordinal: PropTypes.number,
  showOrdinal: PropTypes.bool,
  blockedBySibling: PropTypes.bool,
  onFilledChange: PropTypes.func.isRequired,
  onSavingChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MedicineStatusForm;
